<!-- ============================================================================ -->
<!-- TASK LIST SHELL — Conteneur réutilisable de liste de tâches               -->
<!-- ============================================================================ -->

<!-- ============================================================ -->
<!-- LOADING — Skeleton 3 lignes                                  -->
<!-- ============================================================ -->
@if (loading) {
  <div class="task-shell__skeleton">
    @for (i of [1, 2, 3]; track i) {
      <div class="task-shell__skeleton-item"></div>
    }
  </div>
}

<!-- ============================================================ -->
<!-- EMPTY STATE — même présentation que les notes du galet       -->
<!-- ============================================================ -->
@if (!loading && !hasTasks && !createRequested) {
  <div class="task-shell__empty">
    <div class="task-shell__empty-icon">
      <i
        class="pi"
        [class.pi-search]="isFiltered"
        [class.pi-check]="!isFiltered && isDoneView"
        [class.pi-list]="!isFiltered && !isDoneView"
      ></i>
    </div>
    @if (isFiltered) {
      <h4 class="task-shell__empty-title">Aucun résultat</h4>
      <p class="task-shell__empty-description">Aucune tâche ne correspond à votre recherche ou à vos filtres</p>
    } @else {
      <h4 class="task-shell__empty-title">{{ emptyTitle }}</h4>
      @if (showInlineCreate && objectSections !== "done") {
        <p class="task-shell__empty-description">
          @if (showAllLink) {
            Créez une tâche rattachée ou consultez toutes les tâches
          } @else {
            Notez ce qu'il reste à faire, avec une échéance et un responsable
          }
        </p>
        <div class="task-shell__empty-action">
          <eva-button
            label="Créer une tâche"
            icon="heroPlus"
            severity="primary"
            [block]="true"
            (onClick)="startCreate()"
          />
        </div>
        @if (showAllLink) {
          <div class="task-shell__empty-action task-shell__empty-action--secondary">
            <eva-button
              label="Voir toutes les tâches"
              icon="heroListBullet"
              severity="secondary"
              [block]="true"
              [text]="true"
              (onClick)="showAllRequested.emit()"
            />
          </div>
        }
      } @else if (isDoneView) {
        <!-- « Voir toutes les tâches terminées » : l'onglet Terminées est conservé en portée globale -->
        <p class="task-shell__empty-description">Les tâches cochées apparaîtront ici</p>
        @if (showAllLink) {
          <div class="task-shell__empty-action">
            <eva-button
              label="Voir toutes les tâches terminées"
              icon="heroListBullet"
              severity="secondary"
              [block]="true"
              [text]="true"
              (onClick)="showAllRequested.emit()"
            />
          </div>
        }
      } @else if (showAllLink) {
        <p class="task-shell__empty-description">Consultez toutes les tâches</p>
        <div class="task-shell__empty-action">
          <eva-button
            label="Voir toutes les tâches"
            icon="heroListBullet"
            severity="secondary"
            [block]="true"
            [text]="true"
            (onClick)="showAllRequested.emit()"
          />
        </div>
      } @else {
        <p class="task-shell__empty-description">Aucune tâche pour le moment</p>
      }
    }
  </div>
}

<!-- ============================================================ -->
<!-- SECTIONS                                                     -->
<!-- ============================================================ -->
@if (!loading && (hasTasks || createRequested)) {
  <div class="task-shell__sections">
    @for (section of sections; track section.key) {
      <div class="task-shell__section">
        <!-- En-tête de section (masqué si titre vide — mode all-flat) -->
        <!-- Une seule section affichée (onglet du galet) : l'onglet suffit, pas d'en-tête -->
        @if (section.title && !(context === "object" && objectSections !== "both")) {
          <button
            type="button"
            class="task-shell__section-header"
            [class.task-shell__section-header--overdue]="section.key === 'overdue'"
            (click)="toggleSection(section)"
          >
            <i class="pi" [class.pi-chevron-down]="!section.collapsed" [class.pi-chevron-right]="section.collapsed"></i>
            <!-- Même grammaire que les sections des notes : eva-tag arrondie -->
            <eva-tag [preset]="sectionPreset(section)" size="small" [rounded]="true" />
            <!-- Compteur seulement pour le retard ou une section repliée : sinon la liste le montre -->
            @if (section.tasks.length && (section.collapsed || section.key === "overdue")) {
              <span class="task-shell__section-count">{{ section.tasks.length }}</span>
            }
          </button>
        }

        <!-- Corps de section -->
        @if (!section.collapsed) {
          <div
            class="task-shell__section-body"
            [class.task-shell__section-body--empty]="!section.tasks.length"
            [id]="section.dropListId"
            cdkDropList
            [cdkDropListData]="section.tasks"
            [cdkDropListConnectedTo]="section.connectedTo || []"
            [cdkDropListDisabled]="!enableDragDrop"
            (cdkDropListDropped)="onDrop($event, section)"
          >
            @for (item of getSectionDisplayItems(section); track trackByDisplayItem($index, item)) {
              <!-- Lot (wrapper cdkDrag pour réordonnancement) -->
              @if (isSectionLot(item)) {
                <div
                  class="task-shell__lot-wrapper"
                  cdkDrag
                  [cdkDragData]="item.lot"
                  [cdkDragDisabled]="!enableDragDrop"
                >
                  <ng-template cdkDragPreview>
                    <div class="task-shell__lot-drag-preview">
                      <i class="pi pi-bars"></i>
                      <span>{{ item.lot.title }}</span>
                      <span class="task-shell__lot-drag-preview-count"
                        >&nbsp;· {{ item.tasks.length }} tâche{{ item.tasks.length > 1 ? "s" : "" }}</span
                      >
                    </div>
                  </ng-template>
                  <app-task-lot-row
                    [lot]="item.lot"
                    [tasks]="item.tasks"
                    [employees]="employees"
                    [compact]="compact"
                    [selectionMode]="selectionMode"
                    [selectedTaskIds]="selectedTaskIds"
                    [editingTaskId]="editingTaskId"
                    [showObjectLink]="showObjectLink"
                    [showAssignees]="showAssignees"
                    [color]="getLotColor(item.lot)"
                    [contextObjectLink]="context !== 'object' ? objectFilter : null"
                    [autoFocus]="item.lot.lot_id === newLotId"
                    [enableDragDrop]="enableDragDrop"
                    [connectedDropLists]="getLotConnections(item.lot)"
                    [availableLots]="lots"
                    (lotRenamed)="onLotRenamed($event)"
                    (lotDeleted)="onLotDeleted($event)"
                    (taskStatusChange)="onStatusChange($event)"
                    (taskClick)="onTaskClick($event)"
                    (taskEdited)="onTaskEdited($event)"
                    (editCancelled)="onEditCancelled()"
                    (taskDeleted)="onTaskDeleted()"
                    (selectionToggle)="onTaskSelectionToggle($event)"
                    (contextMenuRequested)="onTaskContextMenu($event)"
                    (taskRemovedFromLot)="onTaskRemovedFromLot($event)"
                    (taskDroppedOnLot)="onDropOnLot($event)"
                    (addTaskRequested)="onAddTaskForLot($event)"
                    (markAllDoneRequested)="onMarkAllDone($event)"
                    (moveToLotRequested)="onMoveToLot($event)"
                    (tasksReordered)="onTasksReordered($event)"
                    (taskMenuAction)="onMenuAction($event)"
                  />
                </div>

                <!-- Groupe par objet lié -->
              } @else if (isObjectGroup(item)) {
                <div class="task-shell__object-group">
                  <div class="task-shell__object-group-header">
                    <eva-tag [preset]="getObjectGroupPreset(item.link)" size="small" [rounded]="true" />
                  </div>
                  @for (
                    subItem of item.items;
                    track subItem.kind === "lot" ? subItem.lot.lot_id : subItem.task.task_id
                  ) {
                    @if (isGroupSubLot(subItem)) {
                      <app-task-lot-row
                        [lot]="subItem.lot"
                        [tasks]="subItem.tasks"
                        [employees]="employees"
                        [compact]="compact"
                        [selectionMode]="selectionMode"
                        [selectedTaskIds]="selectedTaskIds"
                        [editingTaskId]="editingTaskId"
                        [showAssignees]="showAssignees"
                        [color]="getLotColor(subItem.lot)"
                        [contextObjectLink]="context !== 'object' ? objectFilter : null"
                        (lotRenamed)="onLotRenamed($event)"
                        (lotDeleted)="onLotDeleted($event)"
                        (taskStatusChange)="onStatusChange($event)"
                        (taskClick)="onTaskClick($event)"
                        (taskEdited)="onTaskEdited($event)"
                        (editCancelled)="onEditCancelled()"
                        (taskDeleted)="onTaskDeleted()"
                        (selectionToggle)="onTaskSelectionToggle($event)"
                        (contextMenuRequested)="onTaskContextMenu($event)"
                        (taskRemovedFromLot)="onTaskRemovedFromLot($event)"
                        (addTaskRequested)="onAddTaskForLot($event)"
                        (markAllDoneRequested)="onMarkAllDone($event)"
                      />
                    } @else {
                      @if (editingTaskId === subItem.task.task_id) {
                        <app-task-inline-edit
                          [task]="subItem.task"
                          [employees]="employees"
                          (taskUpdated)="onTaskEdited($event)"
                          (cancelled)="onEditCancelled()"
                          (deleted)="onTaskDeleted()"
                        />
                      } @else {
                        <app-task-row
                          [task]="subItem.task"
                          [employees]="employees"
                          [compact]="compact"
                          [showObjectLink]="showObjectLink"
                          [showAssignees]="showAssignees"
                          [selected]="selectedTaskIds.has(subItem.task.task_id)"
                          [selectionMode]="selectionMode"
                          (statusChange)="onStatusChange($event)"
                          (taskClick)="onTaskClick($event)"
                          (selectionToggle)="onTaskSelectionToggle($event)"
                          (contextMenuRequested)="onTaskContextMenu($event)"
                          (menuAction)="onMenuAction($event)"
                        />
                      }
                    }
                  }
                </div>

                <!-- Tâche seule -->
              } @else {
                @if (editingTaskId === item.task.task_id) {
                  <!-- Formulaire d'édition inline à la place de la ligne -->
                  <app-task-inline-edit
                    [task]="item.task"
                    [employees]="employees"
                    (taskUpdated)="onTaskEdited($event)"
                    (cancelled)="onEditCancelled()"
                    (deleted)="onTaskDeleted()"
                  />
                } @else {
                  <div
                    cdkDrag
                    [cdkDragData]="item.task"
                    [cdkDragDisabled]="!enableDragDrop || !!editingTaskId || !canDragReorder"
                    class="task-shell__item"
                  >
                    <app-task-row
                      [task]="item.task"
                      [employees]="employees"
                      [compact]="compact"
                      [showDragHandle]="enableDragDrop && viewMode !== 'all-flat' && sortBy === 'manual'"
                      [showObjectLink]="context !== 'object'"
                      [showAssignees]="showAssignees"
                      [selected]="selectedTaskIds.has(item.task.task_id)"
                      [selectionMode]="selectionMode"
                      (statusChange)="onStatusChange($event)"
                      (taskClick)="onTaskClick($event)"
                      (selectionToggle)="onTaskSelectionToggle($event)"
                      (contextMenuRequested)="onTaskContextMenu($event)"
                      (menuAction)="onMenuAction($event)"
                    />
                  </div>
                }
              }
            }
          </div>

          <!-- Inline create (si activé et que ce n'est pas la section "done") -->
          @if (showInlineCreate && section.key !== "done" && section.key !== "overdue") {
            <app-task-inline-create
              [employees]="employees"
              [defaultObjectsLinks]="defaultObjectLinks"
              [defaultDate]="getDefaultDateForSection(section.key)"
              [defaultAssignee]="getDefaultAssigneeForSection()"
              [showTrigger]="showInlineCreateTrigger"
              [stayOpenAfterCreate]="inlineCreateStayOpen"
              [hideObjectLinks]="context === 'object'"
              (taskCreated)="onTaskCreated($event)"
            />
          }
        }
      </div>
    }
  </div>
}

<!-- ============================================================ -->
<!-- MENU CONTEXTUEL — Créer un lot                               -->
<!-- ============================================================ -->
@if (contextMenuVisible) {
  <div
    class="task-shell__context-menu"
    [style.left.px]="contextMenuX"
    [style.top.px]="contextMenuY"
    [style.visibility]="contextMenuPlaced ? 'visible' : 'hidden'"
    (click)="$event.stopPropagation()"
  >
    @if (contextMenuTask) {
      <button type="button" class="task-shell__context-menu-item" (click)="onContextMenuAction('edit')">
        <i class="pi pi-pencil"></i>
        Modifier
      </button>
      <button
        type="button"
        class="task-shell__context-menu-item task-shell__context-menu-item--danger"
        (click)="onContextMenuAction('delete')"
      >
        <i class="pi pi-trash"></i>
        Supprimer
      </button>
      <div class="task-shell__context-menu-divider"></div>
    }
    <button
      type="button"
      class="task-shell__context-menu-item"
      [class.task-shell__context-menu-item--disabled]="!canCreateLot"
      (click)="canCreateLot && createLot()"
    >
      <i class="pi pi-folder-open"></i>
      Créer un lot
      @if (!lotContainerId) {
        <span class="task-shell__context-menu-hint">Depuis un chantier ou une offre</span>
      } @else if (selectedTaskIds.size >= 2) {
        <span class="task-shell__context-menu-count">{{ selectedTaskIds.size }}</span>
      } @else {
        <span class="task-shell__context-menu-hint">Sélectionnez ≥ 2 tâches</span>
      }
    </button>
  </div>
}
